"use client";

// BlogRail — the landing's window onto what we've actually published.
//
// It reuses the COMPANY page's own card (`.co-card` → face · title · dek ·
// byline, with PostFace resolving a real image or the hosted generated card),
// because a second species of blog card would be a second design language for
// the same object. Only the RAIL around them is new: one horizontal row that
// scrolls, rather than the company grid.
//
// ORDER: the highlighted post (`pinned`) takes the left-most seat, then
// everything else newest-first — the order the fetcher already returns. Posts
// come from `fetchPublicCompanyPosts()` (published blog/update only, cookie-less)
// so this rail is live the moment something publishes and can never show a
// draft.

import Link from "next/link";
import PostFace from "../../company/PostFace";
import { relTime } from "../../../lib/os/tree";
import type { CompanyPost } from "../../../lib/company/types";

export default function BlogRail({ posts }: { posts: CompanyPost[] }) {
  // pinned first, then the fetcher's own published_at order (newest first)
  const ordered = [...posts].sort((a, b) => Number(b.pinned) - Number(a.pinned));
  const shown = ordered.slice(0, 8);
  if (!shown.length) return null;

  return (
    <div className="t1-blog__rail">
      {shown.map((p, i) => (
        <Link key={p.id} className="co-card t1-blog__card" href={`/company/${p.slug}`}>
          <span className="co-card__media" data-empty={!p.media?.[0] || undefined}>
            <PostFace post={p} author={null} />
          </span>
          <span className="co-card__t">{p.title}</span>
          {p.dek && <span className="co-card__d">{p.dek}</span>}
          {/* ⚠️ suppressHydrationWarning: relTime is computed from `now`, so the
              server's string ("59m") and the client's a moment later ("1h") can
              legitimately differ — React counted that as a hydration mismatch
              (#418) on the live page. The value is cosmetic and the client's is
              the more correct one, so the difference is declared, not fought. */}
          <span className="co-by" suppressHydrationWarning>
            {p.category ? `${p.category} · ` : ""}
            {relTime(p.published_at ?? p.created_at)}
            {i === 0 && p.pinned ? " · highlighted" : ""}
          </span>
        </Link>
      ))}
    </div>
  );
}
